import React, { useState } from 'react'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineDot from '@mui/lab/TimelineDot'
import Typography from '@mui/material/Typography'
import Pagination from '@mui/material/Pagination'
import { parseISO, differenceInMinutes } from 'date-fns'
import useFetch from 'hooks/useFetch'

import { formatLocal, formatUTC } from 'utils/date'

import { translate } from 'locales'

// Styles
import { Title, Loading, CardDate } from 'components'
import noData from 'assets/images/no-data.svg'
import * as S from './styles'
import {
  Timeline,
  TimelineContent,
  TimelineOppositeContent,
  AccordionHeadingDiv,
} from '../timelineStyles'

import TimeLineResume from './TimeLineResume'

// Interfaces
import { IComponentProps } from './interfaces'

const History: React.FunctionComponent<IComponentProps> = ({
  doctorName,
  // scheduleUUID,
  schedule,
  specialty,
}) => {
  // States
  const [page, setPage] = useState(1)

  const { data: medical, isLoading } = useFetch({
    url: `/doctors/v2/medical-record/user/${schedule.user_id}/psico/${specialty}?page=${page}&limit=6`,
    options: {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  })

  const timeConsultation = (e: any, t: any) => {
    const startConsultation = parseISO(e)
    const endConsultation = parseISO(t)

    const consultationDuration = differenceInMinutes(
      endConsultation,
      startConsultation
    )
    return consultationDuration
  }

  return (
    <S.SectionContent>
      {isLoading && (
        <Loading title={translate('medicalRecord.loadingHistoric')} />
      )}

      <div>
        <span>{formatLocal(new Date(), 'EEEE, dd/MM/yyyy')}</span>
        <p>{`${translate('medicalRecord.by')} ${doctorName}`}</p>
      </div>

      <div className="subsection">
        <Title text={translate('medicalRecord.historicPatiente')} />
        <div className="subsection-item">
          <Timeline position="right">
            {medical?.medicalRecords?.length === 0 && (
              <S.NoData>
                <img
                  src={noData}
                  alt="Icone simbolizando que não existe histórico do paciente"
                />
                <span>{translate('medicalRecord.notHistoricPatiente')}</span>
              </S.NoData>
            )}

            {medical?.medicalRecords &&
              medical?.medicalRecords?.map((record: any) => (
                <TimelineItem key={record.id}>
                  <TimelineOppositeContent>
                    <CardDate
                      day={formatUTC(record.created_at, 'dd')}
                      month={formatUTC(record.created_at, 'MMM')}
                      year={formatUTC(record.created_at, 'yyyy')}
                    />
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot />
                    <TimelineConnector />
                  </TimelineSeparator>
                  <TimelineContent>
                    <Accordion>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1a-content"
                        id={record.id}
                      >
                        <AccordionHeadingDiv
                          style={{
                            marginTop: 0,
                            display: 'flex',
                            flex: 1,
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Typography>
                            {`${translate('medicalRecord.by')} Dr(a). ${
                              record?.scheduling?.doctor?.name
                            }`}
                            <Typography>
                              {`${translate('schedule.scheduleDate')}: ${formatLocal(record.scheduling.from_date_time, 'dd/MM/yyyy')}`}
                            </Typography>
                          </Typography>
                        </AccordionHeadingDiv>
                        <div>
                          <Typography>
                            {timeConsultation(
                              record.scheduling?.from_date_time,
                              record.scheduling?.to_date_time
                            )}{' '}
                            min {`(${record?.scheduling?.specialty_name})`}
                          </Typography>
                        </div>
                      </AccordionSummary>
                      <AccordionDetails style={{ display: 'block' }}>
                        <TimeLineResume
                          doctorName=""
                          scheduleUUID={schedule?.id}
                          record={record}
                        />
                      </AccordionDetails>
                    </Accordion>
                  </TimelineContent>
                </TimelineItem>
              ))}
          </Timeline>
        </div>
        <S.ContentPagination>
          {medical?.totalPages > 1 && (
            <Pagination
              count={medical?.totalPages}
              onChange={(event: React.ChangeEvent<unknown>, value: number) => {
                setPage(value)
              }}
            />
          )}
        </S.ContentPagination>
      </div>
    </S.SectionContent>
  )
}

export default History
